import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DejaVuSansBoldTtf from 'dejavu-fonts-ttf/ttf/DejaVuSans-Bold.ttf';
import DejaVuSansTtf from 'dejavu-fonts-ttf/ttf/DejaVuSans.ttf';
import { KsefOutgoingRecordDto } from '../Types/ksefOutgoing.types';

interface JsPdfWithAutoTable extends jsPDF {
  lastAutoTable?: {
    finalY: number;
  };
}

interface KsefOutgoingRecordsPdfParams {
  records: KsefOutgoingRecordDto[];
  createdDateFrom?: string;
  createdDateTo?: string;
  totalCount: number;
}

const PDF_FONT_FAMILY = 'DejaVuSans';
const PDF_MARGIN_LEFT = 14;
const PDF_MARGIN_RIGHT = 14;
const TITLE_LINE_HEIGHT = 6;

let cachedRegularFontBinary: string | null = null;
let cachedBoldFontBinary: string | null = null;

const arrayBufferToBinaryString = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    let chunkBinary = '';
    for (let innerIndex = 0; innerIndex < chunk.length; innerIndex += 1) {
      chunkBinary += String.fromCharCode(chunk[innerIndex]);
    }
    binary += chunkBinary;
  }

  return binary;
};

const loadFontBinary = async (fontUrl: string): Promise<string> => {
  const response = await fetch(fontUrl);
  if (!response.ok) {
    throw new Error(`Nie udało się załadować fontu PDF (${response.status}).`);
  }

  const fontBuffer = await response.arrayBuffer();
  return arrayBufferToBinaryString(fontBuffer);
};

const ensureUnicodeFontsRegistered = async (doc: jsPDF): Promise<void> => {
  if (!cachedRegularFontBinary) {
    cachedRegularFontBinary = await loadFontBinary(DejaVuSansTtf);
  }

  if (!cachedBoldFontBinary) {
    cachedBoldFontBinary = await loadFontBinary(DejaVuSansBoldTtf);
  }

  doc.addFileToVFS('DejaVuSans.ttf', cachedRegularFontBinary);
  doc.addFont('DejaVuSans.ttf', PDF_FONT_FAMILY, 'normal');
  doc.addFileToVFS('DejaVuSans-Bold.ttf', cachedBoldFontBinary);
  doc.addFont('DejaVuSans-Bold.ttf', PDF_FONT_FAMILY, 'bold');
};

const toDisplayDateRange = (from?: string, to?: string): string => {
  const normalizedFrom = from?.trim() || '-';
  const normalizedTo = to?.trim() || '-';
  return `${normalizedFrom} - ${normalizedTo}`;
};

const toTableCellValue = (value?: string | null): string => {
  return value && value.trim() ? value : '-';
};

const toDisplayDateTime = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const createKsefOutgoingRecordsPdfBlob = async (
  params: KsefOutgoingRecordsPdfParams
): Promise<Blob> => {
  if (params.records.length === 0) {
    throw new Error('Brak rekordów do eksportu PDF.');
  }

  const doc = new jsPDF({
    orientation: 'portrait',
    format: 'a4',
  }) as JsPdfWithAutoTable;
  await ensureUnicodeFontsRegistered(doc);
  doc.setFont(PDF_FONT_FAMILY, 'normal');

  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PDF_MARGIN_LEFT - PDF_MARGIN_RIGHT;
  const title = 'KSeF Outgoing - Tabela KsefOutgoingInvoices';
  const titleLines = doc.splitTextToSize(title, contentWidth);

  doc.setFontSize(14);
  doc.text(titleLines, PDF_MARGIN_LEFT, 16);

  const headerY = 16 + titleLines.length * TITLE_LINE_HEIGHT + 2;
  doc.setFontSize(10);
  doc.text(
    `Zakres dat utworzenia: ${toDisplayDateRange(params.createdDateFrom, params.createdDateTo)}`,
    PDF_MARGIN_LEFT,
    headerY
  );
  doc.text(
    `Łącznie rekordów: ${params.totalCount}`,
    PDF_MARGIN_LEFT,
    headerY + 5
  );

  autoTable(doc, {
    startY: headerY + 10,
    head: [
      [
        'Lp',
        'Numer faktury',
        'Numer KSeF faktury',
        'Wysłano',
      ],
    ],
    body: params.records.map((record, index) => [
      String(index + 1),
      toTableCellValue(record.invoiceNumber),
      toTableCellValue(record.ksefInvoiceNumber),
      toDisplayDateTime(record.submittedAtUtc),
    ]),
    theme: 'grid',
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      font: PDF_FONT_FAMILY,
      overflow: 'linebreak',
      cellWidth: 'wrap',
      valign: 'middle',
    },
    headStyles: { fontStyle: 'bold', font: PDF_FONT_FAMILY },
    columnStyles: {
      0: { halign: 'right', cellWidth: 12 },
      1: { cellWidth: 46 },
      2: { cellWidth: 90 },
      3: { cellWidth: 34 },
    },
  });

  return doc.output('blob');
};

export const downloadKsefOutgoingRecordsPdf = (blob: Blob, fileName: string): void => {
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error('Wygenerowany PDF tabeli KSeF outgoing jest pusty.');
  }

  const url = window.URL.createObjectURL(blob);
  try {
    const link = window.document.createElement('a');
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(url);
  }
};
